import dayjs from 'dayjs';

const toNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Resolve Indonesia time zone from attendance coordinates.
 *
 * The borders of WIB/WITA/WIT follow administrative regions rather than a
 * single longitude. These bounds cover the major Indonesian island groups:
 * - WIB  : Sumatra, Java, West/Central Kalimantan
 * - WITA : Bali, Nusa Tenggara, South/East/North Kalimantan, Sulawesi
 * - WIT  : Maluku, North Maluku, Papua
 *
 * Falls back to WIB when coordinates are unavailable/invalid because the
 * attendance API currently stores the time value in WIB.
 */
export const getIndonesiaTimeZone = (latitude, longitude) => {
    const lat = toNumber(latitude);
    const lon = toNumber(longitude);

    if (lat === null || lon === null) {
        return { label: 'WIB', offsetFromWib: 0 };
    }

    // Papua and islands east of Papua/Maluku.
    if (lon >= 130) {
        return { label: 'WIT', offsetFromWib: 2 };
    }

    // Maluku & North Maluku. The longitude + latitude guard keeps eastern
    // Nusa Tenggara/Sulawesi in WITA while classifying Ambon/Ternate, etc. WIT.
    if (lon >= 126 && lat >= -8.4 && lat <= 4.5) {
        return { label: 'WIT', offsetFromWib: 2 };
    }

    // Sulawesi.
    if (lon >= 118 && lon < 126 && lat >= -6.8 && lat <= 2.5) {
        return { label: 'WITA', offsetFromWib: 1 };
    }

    // Bali, NTB and NTT.
    if (lon >= 114.42 && lon < 126 && lat < -7) {
        return { label: 'WITA', offsetFromWib: 1 };
    }

    // East / North Kalimantan.
    if (lon >= 115 && lon < 119 && lat >= -4.5 && lat <= 4.8) {
        return { label: 'WITA', offsetFromWib: 1 };
    }

    // South Kalimantan (administrative border is not a straight meridian,
    // therefore this is a coordinate approximation around the province).
    if (lon >= 114.45 && lon < 116.5 && lat < -1.45 && lat >= -4.5) {
        return { label: 'WITA', offsetFromWib: 1 };
    }

    return { label: 'WIB', offsetFromWib: 0 };
};

/**
 * Attendance API time is currently emitted in WIB. Convert it to the local
 * Indonesian zone resolved from the attendance coordinate.
 */
export const localizeAttendanceTime = (date, time, latitude, longitude) => {
    const zone = getIndonesiaTimeZone(latitude, longitude);

    if (!date || !time) {
        return {
            date: date ? dayjs(date).format('YYYY-MM-DD') : '',
            time: '',
            zone: zone.label,
            offsetFromWib: zone.offsetFromWib,
        };
    }

    const base = dayjs(`${dayjs(date).format('YYYY-MM-DD')} ${time}`);
    if (!base.isValid()) {
        return {
            date: dayjs(date).format('YYYY-MM-DD'),
            time,
            zone: zone.label,
            offsetFromWib: zone.offsetFromWib,
        };
    }

    const localized = base.add(zone.offsetFromWib, 'hour');
    return {
        date: localized.format('YYYY-MM-DD'),
        time: localized.format('HH:mm'),
        zone: zone.label,
        offsetFromWib: zone.offsetFromWib,
    };
};
